import { useState, useEffect, useMemo } from 'react';
import { exportAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, FileSpreadsheet, FileText, Loader2, FolderArchive, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ExportPage() {
  const [exportType, setExportType] = useState('csv');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [years, setYears] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [zipYear, setZipYear] = useState('');
  const [zipMonth, setZipMonth] = useState('all');
  const [zipLoading, setZipLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await exportAPI.receiptsOverview();
        const list = res.data.years || [];
        setYears(list);
        if (list.length) setZipYear(String(list[0].year));
      } catch {
        setYears([]);
      } finally {
        setOverviewLoading(false);
      }
    })();
  }, []);

  const selectedYear = useMemo(
    () => years.find((y) => String(y.year) === zipYear),
    [years, zipYear],
  );
  const selectedScope = useMemo(() => {
    if (!selectedYear) return null;
    if (zipMonth === 'all') return selectedYear;
    return selectedYear.months.find((m) => String(m.month) === zipMonth) || null;
  }, [selectedYear, zipMonth]);

  const handleZipDownload = async () => {
    if (!zipYear) return;
    setZipLoading(true);
    try {
      const res = await exportAPI.receiptsZip(Number(zipYear), zipMonth === 'all' ? null : Number(zipMonth));
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = `Belege_${zipYear}${zipMonth === 'all' ? '' : `-${String(zipMonth).padStart(2, '0')}`}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      const files = res.headers['x-file-count'];
      toast.success(`ZIP erstellt${files ? ` – ${files} Belege enthalten` : ''}`);
    } catch (err) {
      toast.error(err.response?.status === 404
        ? 'Für diesen Zeitraum gibt es keine Eingangsrechnungen'
        : 'ZIP konnte nicht erstellt werden');
    } finally {
      setZipLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await exportAPI.generate({
        export_type: exportType,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      setResult(res.data);
      toast.success('Export erfolgreich erstellt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export-Fehler');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([result.content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = result.filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-8" data-testid="export-page">
      <div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Export</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Daten für den Steuerberater exportieren (CSV / DATEV-Format)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Config */}
        <Card className="bg-card border border-border/50" data-testid="export-config-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-primary" />
              Export konfigurieren
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Format</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger className="bg-background h-10" data-testid="export-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (Standard)</SelectItem>
                  <SelectItem value="datev">DATEV-Format</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Von</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-background h-10" data-testid="export-date-from" />
              </div>
              <div className="space-y-2">
                <Label>Bis</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-background h-10" data-testid="export-date-to" />
              </div>
            </div>

            <div className="bg-accent/50 rounded-lg p-4 text-sm text-muted-foreground space-y-1">
              {exportType === 'datev' ? (
                <>
                  <p className="font-medium text-foreground">DATEV-Format</p>
                  <p>Kompatibel mit DATEV Unternehmen Online. Enthält Buchungssätze im Standard-DATEV-Format.</p>
                </>
              ) : (
                <>
                  <p className="font-medium text-foreground">CSV-Format</p>
                  <p>Universelles Tabellenformat. Kompatibel mit Excel, Google Sheets und anderen Tools.</p>
                </>
              )}
            </div>

            <Button onClick={handleExport} disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 shadow-[0_0_15px_rgba(0,229,255,0.3)]" data-testid="generate-export-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {loading ? 'Wird erstellt...' : 'Export generieren'}
            </Button>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="bg-card border border-border/50" data-testid="export-preview-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileText className="w-4 h-4 text-secondary" />
              Vorschau
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="bg-background rounded-lg p-4 max-h-80 overflow-auto">
                  <pre className="text-xs font-mono-data text-muted-foreground whitespace-pre-wrap">{result.content}</pre>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{result.filename}</span>
                  <Button onClick={handleDownload} variant="outline" data-testid="download-export-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Herunterladen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center text-muted-foreground">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Noch kein Export erstellt</p>
                <p className="text-sm mt-1">Konfigurieren Sie den Export und klicken Sie auf "Export generieren"</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Belegarchiv */}
      <Card className="bg-card border border-border/50" data-testid="receipt-archive-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FolderArchive className="w-4 h-4 text-emerald-400" />
            Belege für das Finanzamt
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Alle Originalbelege der Eingangsrechnungen als ZIP – nach Monaten sortiert, mit
            Belegliste als CSV. Ideal für Betriebsprüfung oder Steuerberater.
          </p>

          {overviewLoading ? (
            <div className="py-6 text-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Belege werden gezählt …
            </div>
          ) : years.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm" data-testid="receipt-archive-empty">
              Noch keine Eingangsrechnungen vorhanden.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Jahr</Label>
                  <Select value={zipYear} onValueChange={(v) => { setZipYear(v); setZipMonth('all'); }}>
                    <SelectTrigger className="bg-background h-10" data-testid="receipt-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y.year} value={String(y.year)}>
                          {y.year} · {y.total} Rechnungen
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Zeitraum</Label>
                  <Select value={zipMonth} onValueChange={setZipMonth}>
                    <SelectTrigger className="bg-background h-10" data-testid="receipt-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Ganzes Jahr</SelectItem>
                      {(selectedYear?.months || []).map((m) => (
                        <SelectItem key={m.month} value={String(m.month)}>
                          {m.label} · {m.total} Rechnungen
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedScope && (
                <div className="bg-accent/50 rounded-lg p-4 text-sm space-y-1" data-testid="receipt-archive-summary">
                  <p className="text-foreground font-medium">
                    {selectedScope.with_receipt} von {selectedScope.total} Rechnungen haben einen hinterlegten Beleg
                  </p>
                  {selectedScope.total > selectedScope.with_receipt && (
                    <p className="text-amber-400 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {selectedScope.total - selectedScope.with_receipt} ohne Datei – diese stehen in FEHLENDE_BELEGE.txt
                    </p>
                  )}
                </div>
              )}

              <Button
                onClick={handleZipDownload}
                disabled={zipLoading || !selectedScope?.total}
                className="w-full h-11 bg-emerald-500/90 text-white hover:bg-emerald-500"
                data-testid="download-receipts-zip-btn"
              >
                {zipLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderArchive className="w-4 h-4 mr-2" />}
                {zipLoading ? 'ZIP wird gepackt …' : 'Belege als ZIP herunterladen'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
